import React, { createContext, useContext, useEffect, useState } from "react";
import { api, formatApiError } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  async function resolveDemoWorkspace(candidate) {
    if (candidate?.account_tier !== "demo" || candidate?.role === "admin") return candidate;
    try {
      let current = candidate;
      if (current.workspace_role !== "viewer") {
        const { data: memberships } = await api.get("/team/memberships");
        const firstViewer = (memberships || []).find((membership) => membership.role === "viewer");
        if (!firstViewer) return current;
        localStorage.setItem("rg_workspace", firstViewer.owner_id);
        const { data } = await api.get("/auth/me");
        current = data;
      }
      return current;
    } catch {
      localStorage.removeItem("rg_workspace");
      return candidate;
    }
  }

  useEffect(() => {
    (async () => {
      const token = localStorage.getItem("rg_token");
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const { data } = await api.get("/auth/me");
        setUser(await resolveDemoWorkspace(data));
      } catch (e) {
        localStorage.removeItem("rg_token");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function login(email, password) {
    try {
      localStorage.removeItem("rg_workspace");
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("rg_token", data.token);
      setUser(data);
      let resolvedUser = data;
      try {
        const { data: currentUser } = await api.get("/auth/me");
        resolvedUser = await resolveDemoWorkspace(currentUser);
        setUser(resolvedUser);
      } catch {
        // The valid login response remains usable if profile refresh is temporarily unavailable.
      }
      return { ok: true, user: resolvedUser };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  }

  async function register(payload) {
    try {
      localStorage.removeItem("rg_workspace");
      const { data } = await api.post("/auth/register", payload);
      localStorage.setItem("rg_token", data.token);
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  }

  async function logout() {
    try { await api.post("/auth/logout"); } catch (e) { /* logout is best-effort */ }
    localStorage.removeItem("rg_token");
    setUser(null);
  }

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
